import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Package } from "lucide-react";
import { IOrder, IOrderGoods } from "../../models/Order.model";

type GoodsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedOrder: IOrder | null;
};

const GoodsModal: React.FC<GoodsModalProps> = ({
  isOpen,
  onClose,
  selectedOrder,
}) => {
  const goods = selectedOrder?.goods || [];

  const totalValue = goods.reduce(
    (sum, item) =>
      sum + parseFloat(item.price?.toString() || "0") * item.quantity,
    0
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className="relative bg-white border border-gray-200 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.3 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                  <Package className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Order Goods
                  </h2>
                  <p className="text-xs text-gray-500">
                    {goods.length} items • ₹{totalValue.toLocaleString()}
                  </p>
                </div>
              </div>
              <motion.button
                onClick={onClose}
                className="w-8 h-8 bg-white hover:bg-gray-50 rounded flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <X className="w-4 h-4" />
              </motion.button>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-120px)]">
              {goods.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center mx-auto mb-3">
                    <Package className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-gray-600 font-medium">No goods found</p>
                  <p className="text-xs text-gray-400">
                    This order doesn't contain any products
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {goods.map((item: IOrderGoods, idx: number) => (
                    <motion.div
                      key={idx}
                      className="bg-white border border-gray-200 rounded-md p-4 hover:bg-blue-50/30 transition-colors"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                    >
                      {/* Product Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start space-x-2 flex-1">
                          <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                            <Package className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-gray-900 text-sm leading-tight">
                              {item.product?.productName || item.rawMaterial?.name || "Unknown Item"}                            
                              </h3>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-gray-900">
                            ₹
                            {(
                              parseFloat(item.price?.toString() || "0") *
                              item.quantity
                            ).toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-500">
                            {item.quantity} × ₹{item.price}
                          </p>
                        </div>
                      </div>

                      {/* Product Details Grid */}
                      <div className="grid grid-cols-4 gap-3">
                        <div className="text-center p-2 bg-gray-50 rounded">
                          <p className="text-xs text-gray-500">Qty</p>
                          <p className="text-sm font-medium text-gray-900">
                            {item.quantity}
                          </p>
                        </div>

                        <div className="text-center p-2 bg-gray-50 rounded">
                          <p className="text-xs text-gray-500">Price</p>
                          <p className="text-sm font-medium text-gray-900">
                            ₹{item.price}
                          </p>
                        </div>

                        <div className="text-center p-2 bg-gray-50 rounded">
                          <p className="text-xs text-gray-500">GST</p>
                          <p className="text-sm font-medium text-gray-900">
                            {item.gst}%
                          </p>
                        </div>

                        <div className="text-center p-2 bg-gray-50 rounded">
                          <p className="text-xs text-gray-500">HSN</p>
                          <p className="text-xs font-mono text-gray-900">
                            {item.hsn || "N/A"}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GoodsModal;
